import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { tokenStore } from "@/lib/api";

const AuthContext = createContext(null);

function storeTokens(data) {
  // Prefer JWT access_token (email/password, magic link, reset flows).
  // Fall back to session_token (Google OAuth flow).
  const token = data?.access_token || data?.session_token;
  if (token) tokenStore.set(token);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, AuthCallback handles it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    storeTokens(data);
    setUser(data);
    return data;
  }, []);

  const register = useCallback(async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    storeTokens(data);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed; clearing local session anyway.", err);
    }
    tokenStore.clear();
    setUser(null);
  }, []);

  const applyAuthPayload = useCallback((data) => {
    storeTokens(data);
    setUser(data);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, setUser, applyAuthPayload, refresh: checkAuth }),
    [user, loading, login, register, logout, applyAuthPayload, checkAuth],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
